/**
 * Prompt variations: stops repeat users from seeing identical wording every time.
 *
 * The wording lives in the message catalogue (`variations.*` in
 * public/locales/<lang>/translation.json), one array per rotating line. This
 * file used to hold the English lists itself, which put them outside everything
 * that checks the catalogue: a Polish session showed "A pattern has emerged" and
 * "I am ready to..." in English (Andy's iPhone, 2026-10-14). The rotation stays
 * here, in code; the words do not.
 *
 * Selection is deterministic per session (a hash of the session id), so the same
 * session always gets the same line while successive sessions rotate. Every
 * language's array must have the same length, which the key-parity test
 * enforces, so the same index picks the same meaning in every language.
 *
 * Two lists that used to be here are gone: `ground_opener` and `reframe_question`
 * were never read by the app. The guide's opening question and the reframe live
 * on the server (supabase/functions/_shared/facilitatePrompt.ts), which is sent
 * only the index from `getVariationIndex`.
 */

/**
 * Returns which variation index (0-3) to use for a given session.
 * Pass `sessionId` (UUID) or `createdAt` timestamp string.
 * Deterministic: same session always returns same index.
 */
export function getVariationIndex(sessionIdentifier: string): number {
  // Sum char codes for a simple stable hash
  let sum = 0
  for (let i = 0; i < sessionIdentifier.length; i++) {
    sum += sessionIdentifier.charCodeAt(i)
  }
  return sum % 4
}

/**
 * Pick this session's line from a catalogue array.
 * `list` is whatever `t('variations.<name>', { returnObjects: true })` returned.
 * If it is not a usable array (a catalogue that has not loaded yet), returns an
 * empty string rather than a key name or a crash.
 */
export function pickVariation(list: unknown, sessionIdentifier: string): string {
  if (!Array.isArray(list) || list.length === 0) return ''
  const line = list[getVariationIndex(sessionIdentifier) % list.length]
  return typeof line === 'string' ? line : ''
}
